<template>
  <PModal
    title="Signer"
    @open="onOpen"
    :model-value="props.modelValue"
    @update:model-value="emit('update:modelValue', $event)"
    :persistent="isRunning || isOpen || scws.chooseReaderModalProps.open"
    :disable-persistent-animation="scws.chooseReaderModalProps.open"
    @close="emit('close')"
    size="lg"
  >
    <template #default>
      <div class="flex flex-col gap-2" v-if="massSignatureStore.currentMassSignature">
        <p class="text-grey-625">Seuls apparaissent les documents sur lesquels la signature a été prépositionnée.</p>
        <p v-if="!isRunning" class="bg-blue-france-950 p-1.5v rounded text-grey-100 font-bold">
          <span>
            {{ massSignatureStore.totalSignPosCount }}
            {{ plural("signature", massSignatureStore.totalSignPosCount) }}
            {{ replacePlural(massSignatureStore.totalSignPosCount, "est", "sont") }}
            à faire dans {{ massSignatureStore.totalDocs }}
            {{ plural("document", massSignatureStore.totalDocs) }}
          </span>
        </p>
        <div
          class="border-2 border-grey-950 bg-grey-975 p-2 rounded"
          :key="letterFile.id"
          v-for="letterFile in massSignatureStore.currentMassSignature!.letterFiles"
        >
          <h2 class="font-bold text-xl mb-2">{{ letterFile.name }}</h2>
          <ul class="flex flex-col gap-2">
            <li
              v-if="letterFile.documents.length === 0"
              class="bg-white border-grey-950 border-2 p-2 font-bold rounded flex items-center"
            >
              Aucun document à signer dans ce parapheur.
            </li>
            <li
              v-for="document in letterFile.documents"
              :key="document.id"
              class="bg-white border-grey-950 border-2 p-2 font-bold rounded flex items-center gap-1"
            >
              <OhVueIcon :name="getFileIcon(document.name + document.extension)" scale="1.5"></OhVueIcon>
              <PTooltip label="Signature prépositionnée" :offset="5" :delay="500">
                <template #label="{ label }">
                  <div class="whitespace-nowrap">{{ label }}</div>
                </template>
                <div
                  v-if="!isDoneOrError || !isRunning"
                  class="rounded-full bg-green-tilleul-verveine-925 p-1 mr-1 flex"
                >
                  <span class="text-sm">{{ document.signaturePositionsCount }}</span>
                  <OhVueIcon scale="1" :name="postItIcon" class="mt-1v" />
                </div>
              </PTooltip>
              <span>{{ document.name }}</span>
              <PBadge type="info" class="ml-auto" v-if="!document.status"> À signer </PBadge>
              <PBadge type="info" class="ml-auto" v-if="document.status === MassSignatureStatus.PROCESSING">
                <OhVueIcon :name="loadingIcon" animation="spin"></OhVueIcon>
              </PBadge>
              <PBadge
                type="custom"
                class="ml-auto text-purple-glycine-200 bg-purple-glycine-925"
                v-if="document.status === MassSignatureStatus.DONE"
              >
                Signé
              </PBadge>
              <PBadge type="warning" class="ml-auto" v-if="document.status === MassSignatureStatus.ERROR">
                <OhVueIcon :name="warningIcon"></OhVueIcon>
                Non Signé
              </PBadge>
            </li>
          </ul>
        </div>
      </div>
    </template>
    <template #activator="{ open }">
      <slot name="activator" :open="open">
        <PButton
          data-sentry-id="MassSignModalOpenButton"
          v-if="massSignatureStore.selectedLetterFiles.length > 0"
          :mode="massSignatureStore.selectedLetterFiles.length ? 'primary' : 'secondary'"
          @click="open"
          >Signer</PButton
        >
      </slot>
    </template>
    <template #actions="{ close }">
      <div v-if="!isRunning" class="flex flex-col xl:flex-row gap-2 justify-end">
        <div class="grow">
          <p class="mb-1v">La signature utilisée sera</p>
          <div class="flex">
            <div v-if="!isNil(defaultSignatureId)" class="flex items-center border border-grey-625">
              <div class="p-1 h-12 border-r border-grey-900">
                <img :src="defaultSignatureSrc" alt="signature par défaut" class="object-contain w-full h-full" />
              </div>
              <EnhanceSign :id="defaultSignatureId" class="" />
            </div>
          </div>
        </div>
        <div class="flex gap-1 items-end justify-end xl:justify-start" v-if="!isRunning">
          <PButton mode="secondary" @click="close">Annuler</PButton>
          <PButton
            @click="sign(false)"
            :disabled="documentsCounts.total <= 0 || userPrefsStore.signatureObjects.length <= 0"
          >
            Signer
          </PButton>
          <PTooltip
            :disabled="ws.isReady.value"
            label="La signature identité renforcée n'est pas disponible, veuillez rafraichir votre page (F5) et réessayer"
            :delay="500"
            :offset="5"
          >
            <PButton
              v-if="simpleFeatureStore.pades.enabled"
              @click="sign(true)"
              :disabled="!ws.isReady.value || documentsCounts.total <= 0 || userPrefsStore.signatureObjects.length <= 0"
            >
              Signer + identité renforcée
            </PButton>
          </PTooltip>
        </div>
      </div>
      <div v-else>
        <p
          class="bg-success-950 p-1.5v rounded text-grey-100 font-bold -mx-2"
          :class="documentsCounts.error === 0 ? 'bg-success-950' : 'bg-warning-950'"
        >
          <span>
            {{ documentsCounts.signCount }}
            {{ plural("signature", documentsCounts.signCount) }}
            {{ plural("effectuée", documentsCounts.signCount) }}
            dans {{ documentsCounts.total }} {{ plural("document", documentsCounts.total) }}.
            <span v-if="documentsCounts.total - documentsCounts.signed > 0">
              {{ documentsCounts.total - documentsCounts.signed }}
              {{
                replacePlural(
                  documentsCounts.total - documentsCounts.signed,
                  "document n'a pas été signé",
                  "documents n'ont pas été signés"
                )
              }}
            </span>
            <span v-else>
              {{
                documentsCounts.total > 1 ? "Tous les documents ont été complètement signés" : "Le document est signé"
              }}
            </span>
          </span>
        </p>
        <div class="flex flex-col text-center text-xl font-bold" v-if="isDoneOrError">
          <p>
            Voulez-vous passer
            {{ plural("le", massSignatureStore.totalLetterFile) }}
            {{ plural("parapheur", massSignatureStore.totalLetterFile) }}
            à l'étape suivante ?
          </p>
          <div class="flex justify-center items-center gap-2 mt-2">
            <PButton mode="secondary" @click="completeMassSignature(false, close)"> Non </PButton>
            <PButton @click="completeMassSignature(true, close)">Oui</PButton>
          </div>
        </div>
        <div v-else-if="inactivityTimer >= MAX_INACTIVITY" class="flex flex-col gap-1 items-center mt-2">
          <p>
            L'opération semble s'être interrompue pour une raison inconnue.
            <PLink v-if="isPades" to="/sign-help">Consulter la page d'aide à la signature certifiée</PLink>
          </p>
          <PButton mode="primary" color="error" @click="completeMassSignature(false, close)">
            Annuler l'opération
          </PButton>
        </div>
      </div>
    </template>
  </PModal>
</template>

<script setup lang="ts">
import EnhanceSign from "@/app/views/LetterFileForm/Sign/EnhanceSign.vue";
import { type Nullable, PBadge, PButton, PLink, PModal, PTooltip, timeout, useIcons } from "@pasta/box-v2";
import { debounce, flatten, isEqual, isNil } from "lodash";
import { OhVueIcon } from "oh-vue-icons";
import { MdWarningSharp, RiLoaderLine, RiStickyNoteLine } from "oh-vue-icons/icons";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useFileIcon } from "../composable/useFileIcon";
import { usePastaWebsocket } from "../composable/usePastaWebsocket";
import { useSCWS } from "../composable/useSCWS";
import { MassSignatureStatus } from "../dto/mass-signature.dto";
import { useEnhanceSignatureStore } from "../stores/enhance-signature.store";
import { useLetterFileStore } from "../stores/letter-file.store";
import { useMassSignatureStore } from "../stores/mass-signature.store";
import { useSimpleFeatureStore } from "../stores/simple-features.store";
import { useUserPreferencesStore } from "../stores/user-preferences.store";
import { plural, replacePlural } from "../utils/plural";

interface Props {
  modelValue: boolean;
}

const props = defineProps<Props>();

const massSignatureStore = useMassSignatureStore();
const userPrefsStore = useUserPreferencesStore();
const letterFileStore = useLetterFileStore();
const simpleFeatureStore = useSimpleFeatureStore();
const [warningIcon, loadingIcon, postItIcon] = useIcons(MdWarningSharp, RiLoaderLine, RiStickyNoteLine);

const emit = defineEmits<{
  (event: "open"): void;
  (event: "close"): void;
  (event: "update:modelValue", value: boolean): void;
}>();

const scws = useSCWS();

const isOpen = computed(() => {
  return scws.massModalProps.open;
});

const isPades = ref(false);
async function sign(pades: boolean) {
  isPades.value = pades;
  if (pades) {
    await scws.init();
    const unlocked = await scws.unlockPinForSignature();
    if (!unlocked) return;
  }
  await massSignatureStore.start(pades, useEnhanceSignatureStore().getOptions(defaultSignatureId.value!));
  startInactivityTimer();
}

function onOpen() {
  isPades.value = false;
  inactivityTimer.value = 0;
  fetch();
}

const fetch = debounce(async () => {
  if (!massSignatureStore.currentMassSignature?.creationDate) {
    await massSignatureStore.fetchSummary();
  }
  await loadDefaultSignature();
});

const isRunning = computed(() => !!massSignatureStore.currentMassSignature?.creationDate);
const isDoneOrError = computed(() => {
  return [MassSignatureStatus.DONE, MassSignatureStatus.ERROR].includes(
    massSignatureStore.currentMassSignature!.status
  );
});

const inactivityTimer = ref(0);
const MAX_INACTIVITY = computed(() => massSignatureStore.maxInactivity);
const INACTIVTY_MANUAL_FETCH_THRESHOLD = 3_000; // when not receiving any websocket for xxxx ms, we trigger a manual fetch
let timerId: number | null = null;
function startInactivityTimer() {
  const interval = 1_000;
  timerId = window.setInterval(() => {
    inactivityTimer.value = inactivityTimer.value + interval;
  }, interval);
}
function stopInactivityTimer() {
  if (timerId) {
    window.clearInterval(timerId);
  }
}
watch(
  () => inactivityTimer.value,
  async () => {
    if (inactivityTimer.value >= MAX_INACTIVITY.value) {
      stopInactivityTimer();
      if (!isDoneOrError.value) {
        await massSignatureStore.getMine();
      }
    } else if (inactivityTimer.value > INACTIVTY_MANUAL_FETCH_THRESHOLD) {
      const beforeStatuses = flatten(massSignatureStore.currentMassSignature?.letterFiles.map(lf => lf.documents))
        .map(doc => doc.status)
        .sort();
      await massSignatureStore.getMine();
      const afterStatuses = flatten(massSignatureStore.currentMassSignature?.letterFiles.map(lf => lf.documents))
        .map(doc => doc.status)
        .sort();
      if (!isEqual(beforeStatuses, afterStatuses)) {
        inactivityTimer.value = 0;
      }
    }
  }
);
onMounted(async () => {
  await ws.checkActive();
  if (isRunning.value) {
    startInactivityTimer();
  }
});

const documentsCounts = computed(() => {
  return (massSignatureStore.currentMassSignature?.letterFiles || []).reduce(
    (acc, letterFile) => {
      const doneDocs = letterFile.documents.filter(document => document.status === MassSignatureStatus.DONE);
      return {
        total: acc.total + letterFile.documents.length,
        signed: acc.signed + doneDocs.length,
        error:
          acc.error + letterFile.documents.filter(document => document.status === MassSignatureStatus.ERROR).length,
        signCount: acc.signCount + doneDocs.reduce((acc, curr) => acc + curr.signaturePositionsCount, 0),
      };
    },
    { signed: 0, total: 0, error: 0, signCount: 0 }
  );
});

const getFileIcon = useFileIcon();

async function completeMassSignature(next: boolean, close: () => void) {
  stopInactivityTimer();
  scws.logoff();
  const willBeArchived = (massSignatureStore.currentMassSignature?.letterFiles || []).filter(lf => lf.willBeArchived);
  if (
    willBeArchived.length &&
    next &&
    !confirm(
      `Attention, les parapheurs suivants seront archivés (aucune étape dans le flux descendant) : \n\n${willBeArchived
        .map(lf => `[${lf.chronoNumber}] - ${lf.name}`)
        .join("\n")}\n\nVoulez-vous continuer ?`
    )
  ) {
    return;
  }
  const letterFilesIds = (massSignatureStore.currentMassSignature?.letterFiles || []).map(lf => lf.id);
  await massSignatureStore.complete(next);
  letterFileStore.deleteCached(letterFilesIds);
  close();
  await timeout(1000); // TODO [develop] find a better way to avoid problems where bull jobs are not done yet and home is not up to date
  if (letterFilesIds.length === 1) {
    await letterFileStore.fetchCurrent();
  } else {
    await letterFileStore.getHome();
  }
}

const ws = usePastaWebsocket();
let off: Function | null = null;
onMounted(async () => {
  off = await ws.on("mass-signature-complete", "onMassSignatureComplete", (event: any) => {
    massSignatureStore.updateMassSignature(event);
    inactivityTimer.value = 0;
  });
});
onUnmounted(() => {
  off?.();
});

// default signature
const defaultSignatureId = ref<Nullable<number>>(null);
const defaultSignatureSrc = ref<string>();
async function loadDefaultSignature() {
  defaultSignatureId.value = userPrefsStore.defaultSignature?.id || null;
  if (!isNil(defaultSignatureId.value)) {
    defaultSignatureSrc.value = await userPrefsStore.getSignatureLink(defaultSignatureId.value);
  }
}
</script>
